import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  Download, 
  Calendar, 
  BarChart3, 
  FileText,
  Filter,
  Users,
  CreditCard,
  ChevronDown,
  X
} from 'lucide-react';
import { getAllDebts, getAllUsers, getUpcomingEvents } from '../services/api';
import { Debt, User as UserType, Event } from '../types';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const ReportsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Select = styled.select`
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SearchableSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchableInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const DropdownButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const DropdownList = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e1e5e9;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled.div<{ isSelected: boolean }>`
  padding: 12px;
  cursor: pointer;
  background: ${props => props.isSelected ? '#f0f8ff' : 'white'};
  color: ${props => props.isSelected ? '#667eea' : '#333'};
  border-bottom: 1px solid #f0f0f0;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SelectedMember = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 8px;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: #666;
  
  &:hover {
    color: #e74c3c;
  }
`;

const Input = styled.input`
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 2px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const ReportsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const ReportCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    border-color: #667eea;
  }
`;

const ReportIcon = styled.div<{ color: string }>`
  width: 60px;
  height: 60px;
  border-radius: 15px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
  margin-bottom: 20px;
`;

const ReportTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
`;

const ReportDescription = styled.p`
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const ReportActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button<{ variant: 'pdf' | 'excel' }>`
  flex: 1;
  padding: 10px 15px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'pdf' ? `
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  ` : `
    background: #28a745;
    color: white;
    
    &:hover {
      background: #218838;
    }
  `}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 14px;
  font-weight: 500;
`;

const Reports: React.FC = () => {
  const [debts, setDebts] = useState<Debt[]>([]);
  const [members, setMembers] = useState<UserType[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    reportType: 'all',
    dateFrom: '',
    dateTo: '',
    memberId: '',
    debtStatus: 'all'
  });

  // Searchable select states
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<UserType | null>(null);
  const memberDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [debtsData, membersData, eventsData] = await Promise.all([
        getAllDebts(),
        getAllUsers(),
        getUpcomingEvents(100)
      ]);
      
      setDebts(debtsData);
      setMembers(membersData);
      setEvents(eventsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  // Sort members by name
  const sortedMembers = [...members].sort((a, b) => {
    const nameA = `${a.name} ${a.lastName}`.toLowerCase();
    const nameB = `${b.name} ${b.lastName}`.toLowerCase();
    return nameA.localeCompare(nameB, 'he');
  });

  // Filter members based on search term
  const filteredMembers = sortedMembers.filter(member => {
    const fullName = `${member.name} ${member.lastName}`.toLowerCase();
    const phone = member.phone;
    return fullName.includes(memberSearchTerm.toLowerCase()) || 
           phone.includes(memberSearchTerm);
  });

  // Handle member selection
  const handleMemberSelect = (member: UserType) => {
    setSelectedMember(member);
    setFilters({ ...filters, memberId: member.id });
    setMemberSearchTerm(`${member.name} ${member.lastName}`);
    setIsMemberDropdownOpen(false);
  };

  // Clear selected member
  const clearSelectedMember = () => {
    setSelectedMember(null);
    setFilters({ ...filters, memberId: '' });
    setMemberSearchTerm('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target as Node)) {
        setIsMemberDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getFilteredData = () => {
    let filteredDebts = debts;
    let filteredEvents = events;

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filteredDebts = filteredDebts.filter(debt => {
        try {
          let date;
          if (debt.createdAt && typeof debt.createdAt === 'object' && 'toDate' in debt.createdAt) {
            date = debt.createdAt.toDate();
          } else if (debt.createdAt instanceof Date) {
            date = debt.createdAt;
          } else {
            date = new Date(debt.createdAt);
          }
          return date >= fromDate;
        } catch (error) {
          return false;
        }
      });
      filteredEvents = filteredEvents.filter(event => {
        try {
          let date;
          if (event.eventDate && typeof event.eventDate === 'object' && 'toDate' in event.eventDate) {
            date = event.eventDate.toDate();
          } else if (event.eventDate instanceof Date) {
            date = event.eventDate;
          } else {
            date = new Date(event.eventDate);
          }
          return date >= fromDate;
        } catch (error) {
          return false;
        }
      });
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      filteredDebts = filteredDebts.filter(debt => {
        try {
          let date;
          if (debt.createdAt && typeof debt.createdAt === 'object' && 'toDate' in debt.createdAt) {
            date = debt.createdAt.toDate();
          } else if (debt.createdAt instanceof Date) {
            date = debt.createdAt;
          } else {
            date = new Date(debt.createdAt);
          }
          return date <= toDate;
        } catch (error) {
          return false;
        }
      });
      filteredEvents = filteredEvents.filter(event => {
        try {
          let date;
          if (event.eventDate && typeof event.eventDate === 'object' && 'toDate' in event.eventDate) {
            date = event.eventDate.toDate();
          } else if (event.eventDate instanceof Date) {
            date = event.eventDate;
          } else {
            date = new Date(event.eventDate);
          }
          return date <= toDate;
        } catch (error) {
          return false;
        }
      });
    }

    if (filters.memberId) {
      filteredDebts = filteredDebts.filter(debt => debt.memberId === filters.memberId);
    }

    if (filters.debtStatus === 'paid') {
      filteredDebts = filteredDebts.filter(debt => debt.isPaid);
    } else if (filters.debtStatus === 'unpaid') {
      filteredDebts = filteredDebts.filter(debt => !debt.isPaid);
    }

    return { filteredDebts, filteredEvents };
  };

  const generateDebtsReport = () => {
    const { filteredDebts } = getFilteredData();
    
    const reportData = filteredDebts.map(debt => {
      const member = members.find(m => m.id === debt.memberId);
      return {
        'מתפלל': member ? `${member.name} ${member.lastName}` : 'לא ידוע',
        'סוג חוב': debt.description || 'חוב כללי',
        'סכום': debt.amount,
        'נגבה': debt.paidAmount,
        'יתרה': debt.remainingAmount,
        'סטטוס': debt.isPaid ? 'סגור' : 'פתוח',
        'תאריך יצירה': (() => {
          try {
            let date;
            if (debt.createdAt && typeof debt.createdAt === 'object' && 'toDate' in debt.createdAt) {
              date = debt.createdAt.toDate();
            } else if (debt.createdAt instanceof Date) {
              date = debt.createdAt;
            } else {
              date = new Date(debt.createdAt);
            }
            return date.toLocaleDateString('he-IL');
          } catch (error) {
            return 'תאריך לא תקין';
          }
        })()
      };
    });

    return reportData;
  };

  const generateEventsReport = () => {
    const { filteredEvents } = getFilteredData();
    
    const reportData = filteredEvents.map(event => {
      const member = event.memberId ? members.find(m => m.id === event.memberId) : null;
      return {
        'כותרת': event.title,
        'תיאור': event.description || '',
        'תאריך': (() => {
          try {
            let date;
            if (event.eventDate && typeof event.eventDate === 'object' && 'toDate' in event.eventDate) {
              date = event.eventDate.toDate();
            } else if (event.eventDate instanceof Date) {
              date = event.eventDate;
            } else {
              date = new Date(event.eventDate);
            }
            return date.toLocaleDateString('he-IL');
          } catch (error) {
            return 'תאריך לא תקין';
          }
        })(),
        'סוג': event.eventType,
        'מתפלל קשור': member ? `${member.name} ${member.lastName}` : ''
      };
    });

    return reportData;
  };

  const generateMembersReport = () => {
    const reportData = members.map(member => ({
      'שם פרטי': member.name,
      'שם משפחה': member.lastName,
      'טלפון': member.phone,
      'כתובת': member.address || '',
      'טלפון נוסף': member.additionalPhone || '',
      'מנהל': member.isAdmin ? 'כן' : 'לא',
      'תאריך הצטרפות': (() => {
        try {
          let date;
          if (member.createdAt && typeof member.createdAt === 'object' && 'toDate' in member.createdAt) {
            date = member.createdAt.toDate();
          } else if (member.createdAt instanceof Date) {
            date = member.createdAt;
          } else {
            date = new Date(member.createdAt);
          }
          return date.toLocaleDateString('he-IL');
        } catch (error) {
          return 'תאריך לא תקין';
        }
      })()
    }));

    return reportData;
  };

  const exportToPDF = (data: any[], title: string) => {
    const doc = new jsPDF();
    
    // Add Hebrew font support
    doc.setFont('helvetica');
    
    // Title
    doc.setFontSize(20);
    doc.text(title, 14, 22);
    
    // Date range
    if (filters.dateFrom || filters.dateTo) {
      doc.setFontSize(12);
      const dateRange = `תקופה: ${filters.dateFrom || 'מתחילת הזמן'} - ${filters.dateTo || 'עד היום'}`;
      doc.text(dateRange, 14, 35);
    }
    
    // Table headers
    const headers = Object.keys(data[0] || {});
    const colWidth = 180 / headers.length;
    
    doc.setFontSize(10);
    let y = 50;
    
    // Headers
    headers.forEach((header, index) => {
      doc.text(header, 14 + (index * colWidth), y);
    });
    
    y += 10;
    
    // Data rows
    data.slice(0, 20).forEach((row, rowIndex) => {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      
      headers.forEach((header, colIndex) => {
        const value = String(row[header] || '');
        doc.text(value, 14 + (colIndex * colWidth), y);
      });
      
      y += 8;
    });
    
    doc.save(`${title}.pdf`);
  };

  const exportToExcel = (data: any[], title: string) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'דוח');
    XLSX.writeFile(wb, `${title}.xlsx`);
  };

  const getStats = () => {
    const { filteredDebts } = getFilteredData();
    const totalDebts = filteredDebts.reduce((sum, debt) => sum + debt.amount, 0);
    const totalPaid = filteredDebts.reduce((sum, debt) => sum + debt.paidAmount, 0);
    const totalUnpaid = totalDebts - totalPaid;
    const paidDebts = filteredDebts.filter(debt => debt.isPaid).length;
    const unpaidDebts = filteredDebts.filter(debt => !debt.isPaid).length;

    return {
      totalMembers: members.length,
      totalDebts: totalDebts,
      totalPaid: totalPaid,
      totalUnpaid: totalUnpaid,
      paidDebts: paidDebts,
      unpaidDebts: unpaidDebts,
      totalEvents: events.length
    };
  };

  if (loading) {
    return (
      <ReportsContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          טוען נתונים...
        </div>
      </ReportsContainer>
    );
  }

  const stats = getStats();

  return (
    <ReportsContainer>
      <Header>
        <Title>
          <BarChart3 size={28} />
          דוחות וניתוחים
        </Title>
      </Header>

      <FilterSection>
        <FilterRow>
          <FilterGroup>
            <Label>סוג דוח</Label>
            <Select
              value={filters.reportType}
              onChange={(e) => setFilters({ ...filters, reportType: e.target.value })}
            >
              <option value="all">כל הדוחות</option>
              <option value="debts">דוח חובות</option>
              <option value="events">דוח אירועים</option>
              <option value="members">דוח מתפללים</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>מתאריך</Label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>עד תאריך</Label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>מתפלל</Label>
            <SearchableSelectContainer ref={memberDropdownRef}>
              {selectedMember ? (
                <SelectedMember>
                  <span>{selectedMember.name} {selectedMember.lastName}</span>
                  <ClearButton onClick={clearSelectedMember}>
                    <X size={16} />
                  </ClearButton>
                </SelectedMember>
              ) : (
                <>
                  <SearchableInput
                    type="text"
                    placeholder="חיפוש מתפלל לפי שם או טלפון..."
                    value={memberSearchTerm}
                    onChange={(e) => {
                      setMemberSearchTerm(e.target.value);
                      setIsMemberDropdownOpen(true);
                    }}
                    onFocus={() => setIsMemberDropdownOpen(true)}
                  />
                  <DropdownButton onClick={() => setIsMemberDropdownOpen(!isMemberDropdownOpen)}>
                    <ChevronDown size={20} />
                  </DropdownButton>
                </>
              )}
              
              <DropdownList isOpen={isMemberDropdownOpen}>
                <DropdownItem
                  isSelected={!selectedMember}
                  onClick={() => {
                    setSelectedMember(null);
                    setFilters({ ...filters, memberId: '' });
                    setMemberSearchTerm('');
                    setIsMemberDropdownOpen(false);
                  }}
                >
                  <div style={{ fontWeight: '600', color: '#666' }}>
                    כל המתפללים
                  </div>
                </DropdownItem>
                {filteredMembers.map(member => (
                  <DropdownItem
                    key={member.id}
                    isSelected={selectedMember?.id === member.id}
                    onClick={() => handleMemberSelect(member)}
                  >
                    <div>
                      <div style={{ fontWeight: '600' }}>
                        {member.name} {member.lastName}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {member.phone}
                      </div>
                    </div>
                  </DropdownItem>
                ))}
                {filteredMembers.length === 0 && memberSearchTerm && (
                  <DropdownItem isSelected={false}>
                    <div style={{ color: '#666', textAlign: 'center' }}>
                      לא נמצאו מתפללים
                    </div>
                  </DropdownItem>
                )}
              </DropdownList>
            </SearchableSelectContainer>
          </FilterGroup>

          <FilterGroup>
            <Label>סטטוס חוב</Label>
            <Select
              value={filters.debtStatus}
              onChange={(e) => setFilters({ ...filters, debtStatus: e.target.value })}
            >
              <option value="all">כל החובות</option>
              <option value="paid">חובות סגורים</option>
              <option value="unpaid">חובות פתוחים</option>
            </Select>
          </FilterGroup>
        </FilterRow>

        <ButtonGroup>
          <Button variant="secondary" onClick={() => setFilters({
            reportType: 'all',
            dateFrom: '',
            dateTo: '',
            memberId: '',
            debtStatus: 'all'
          })}>
            <Filter size={16} />
            נקה מסננים
          </Button>
        </ButtonGroup>
      </FilterSection>

      <StatsGrid>
        <StatCard>
          <StatValue>{stats.totalMembers}</StatValue>
          <StatLabel>סה"כ מתפללים</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>₪{stats.totalDebts.toLocaleString()}</StatValue>
          <StatLabel>סה"כ חובות</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>₪{stats.totalPaid.toLocaleString()}</StatValue>
          <StatLabel>סה"כ נגבה</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>₪{stats.totalUnpaid.toLocaleString()}</StatValue>
          <StatLabel>יתרה פתוחה</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.paidDebts}</StatValue>
          <StatLabel>חובות סגורים</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.unpaidDebts}</StatValue>
          <StatLabel>חובות פתוחים</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalEvents}</StatValue>
          <StatLabel>סה"כ אירועים</StatLabel>
        </StatCard>
      </StatsGrid>

      <ReportsGrid>
        <ReportCard>
          <ReportIcon color="#667eea">
            <CreditCard size={24} />
          </ReportIcon>
          <ReportTitle>דוח חובות</ReportTitle>
          <ReportDescription>
            דוח מפורט של כל החובות במערכת עם אפשרות לסינון לפי תאריכים ומתפללים
          </ReportDescription>
          <ReportActions>
            <ActionButton
              variant="pdf"
              onClick={() => exportToPDF(generateDebtsReport(), 'דוח חובות')}
            >
              <FileText size={16} />
              PDF
            </ActionButton>
            <ActionButton
              variant="excel"
              onClick={() => exportToExcel(generateDebtsReport(), 'דוח חובות')}
            >
              <Download size={16} />
              Excel
            </ActionButton>
          </ReportActions>
        </ReportCard>

        <ReportCard>
          <ReportIcon color="#28a745">
            <Calendar size={24} />
          </ReportIcon>
          <ReportTitle>דוח אירועים</ReportTitle>
          <ReportDescription>
            דוח מפורט של כל האירועים במערכת עם אפשרות לסינון לפי תאריכים
          </ReportDescription>
          <ReportActions>
            <ActionButton
              variant="pdf"
              onClick={() => exportToPDF(generateEventsReport(), 'דוח אירועים')}
            >
              <FileText size={16} />
              PDF
            </ActionButton>
            <ActionButton
              variant="excel"
              onClick={() => exportToExcel(generateEventsReport(), 'דוח אירועים')}
            >
              <Download size={16} />
              Excel
            </ActionButton>
          </ReportActions>
        </ReportCard>

        <ReportCard>
          <ReportIcon color="#ffc107">
            <Users size={24} />
          </ReportIcon>
          <ReportTitle>דוח מתפללים</ReportTitle>
          <ReportDescription>
            דוח מפורט של כל המתפללים במערכת עם פרטי קשר והעדפות
          </ReportDescription>
          <ReportActions>
            <ActionButton
              variant="pdf"
              onClick={() => exportToPDF(generateMembersReport(), 'דוח מתפללים')}
            >
              <FileText size={16} />
              PDF
            </ActionButton>
            <ActionButton
              variant="excel"
              onClick={() => exportToExcel(generateMembersReport(), 'דוח מתפללים')}
            >
              <Download size={16} />
              Excel
            </ActionButton>
          </ReportActions>
        </ReportCard>
      </ReportsGrid>
    </ReportsContainer>
  );
};

export default Reports;
